import React, { useEffect, useState } from 'react';

import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import {
    CloseOutlined,
    DescriptionOutlined,
    DirectionsCarOutlined,
    DownloadOutlined,
    GroupsOutlined,
    HistoryOutlined,
    InfoOutlined,
    MeetingRoomOutlined,
    PhotoLibraryOutlined,
    RecordVoiceOverOutlined,
    VerifiedUserOutlined
} from '@mui/icons-material';

import { PopupTransition } from 'components/@extended/Transitions';
import { ConfirmModal } from 'components';
import { ConfirmModalType } from 'components/common/ConfirmModal';
import Lightbox from 'components/AccessLogs/Inspector/Lightbox';
import IncidentReportService from 'services/incident-report.service';
import SnackbarNotify from 'utils/snackbar/SnackbarNotify';
import { downloadBlob } from 'utils/helpers/download-blob';
import { useSelector } from 'react-redux';
import { usePermissions } from 'hooks/usePermissions';
import { _userData } from 'store/sagas/Selectors';
import { OrgPermissions } from 'utils/permissions';
import { userTypes } from 'utils/constants';
import { capitalizeFirstLetter, capitalizeFirstLetterMulti } from 'utils/Helpers';
import {
    errorMessageFrom,
    formatDate,
    formatDateTime,
    humanize,
    incidentCategoryLabel,
    incidentSeverityLabel,
    incidentSubtypeDetail,
    severityColor,
    statusColor
} from 'utils/helpers/guard-report.helper';
import IncidentReportEditDialog from './IncidentReportEditDialog';
import IncidentReportViolationDialog from './IncidentReportViolationDialog';

// ==============================|| GUARD REPORTS - INCIDENT DETAIL ||============================== //

const DetailField = ({ label, children, full }) => (
    <Grid item xs={12} sm={full ? 12 : 6}>
        <Typography variant="caption" color="secondary" display="block">
            {label}
        </Typography>
        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {children || '—'}
        </Typography>
    </Grid>
);

// Card wrapper for each section — bordered and titled with an icon so the
// long report reads as distinct blocks instead of one continuous stream.
const SectionCard = ({ icon: Icon, title, children }) => (
    <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 1.5, borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.75 }}>
            <Icon sx={{ fontSize: 19, color: 'text.secondary' }} />
            <Typography variant="subtitle1">{title}</Typography>
        </Stack>
        {children}
    </Paper>
);

// A single item inside a list-type section (parties, vehicles, witnesses...),
// boxed so multiple entries don't visually run into each other.
const ListEntry = ({ children }) => (
    <Box
        sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider'
        }}
    >
        {children}
    </Box>
);

const IncidentReportDetailDialog = ({ orgId, incidentId, onClose, onChanged }) => {
    const open = Boolean(incidentId);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [lightbox, setLightbox] = useState({ open: false });
    const [acting, setActing] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null); // 'close' | 'approve' | null
    const [editOpen, setEditOpen] = useState(false);
    const [violationOpen, setViolationOpen] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    const { hasPermission } = usePermissions();
    const userData = useSelector(_userData);
    // Org-scoped, not root `userData.type` — a manager can be a supervisor at this org and a
    // plain manager elsewhere, matching the API's `orgs[orgId].type` (see `hasOrgUserType`).
    const isSupervisor = userData?.orgs?.[orgId]?.type === userTypes.SUPERVISOR;
    const canView = hasPermission(OrgPermissions.MANAGE_REPORTS) || isSupervisor;
    // Supervisor actions (edit, violation flags, approve) are gated on the org-scoped supervisor
    // type, not on an org permission — matching the API, where those four routes take
    // hasOrgUserType('orgId', SUPERVISOR). A super admin still gets through, same as every other
    // route in this module.
    const canManage = isSupervisor || userData?.type === userTypes.SUPER_ADMIN;

    const closeLightbox = () => setLightbox({ open: false });

    useEffect(() => {
        if (!open || !orgId || !incidentId) return undefined;

        let cancelled = false;
        setLoading(true);
        setReport(null);

        IncidentReportService.getById(orgId, incidentId)
            .then(data => {
                if (!cancelled) setReport(data);
            })
            .catch(e => {
                if (cancelled) return;
                SnackbarNotify.error(
                    `Failed to load incident report - ${errorMessageFrom(e)}`
                );
                onClose();
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // onClose is only invoked on the error path; excluding it keeps the
        // fetch from re-running on every parent re-render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, orgId, incidentId]);

    const applyUpdated = updated => {
        setReport(updated);
        onChanged?.();
    };

    const handleViolationSaved = updated => {
        applyUpdated(updated);
        onClose();
    };

    // Used after a 409 ("someone else acted") — pulls the current state
    // without disturbing the loading spinner or closing the dialog.
    const refreshReport = () => {
        if (!orgId || !incidentId) return;
        IncidentReportService.getById(orgId, incidentId)
            .then(data => setReport(data))
            .catch(e =>
                SnackbarNotify.error(`Failed to refresh incident report - ${errorMessageFrom(e)}`)
            );
    };

    const runAction = async (action, successMessage) => {
        setActing(true);
        try {
            const updated = await action();
            applyUpdated(updated);
            SnackbarNotify.success(successMessage);
            onClose();
        } catch (e) {
            SnackbarNotify.error(errorMessageFrom(e));
            if (e?.response?.status === 409) refreshReport();
        } finally {
            setActing(false);
        }
    };

    const handleAcknowledge = () =>
        runAction(
            () => IncidentReportService.acknowledge(orgId, report.id),
            'Report acknowledged.'
        );

    const handleRemoveViolation = () =>
        runAction(
            () => IncidentReportService.clearViolation(orgId, report.id),
            'Violation removed.'
        );

    // Self-contained PDF — no signed URLs to expire, so the blob can be saved directly.
    // errorMessageFrom isn't used here: an error response under responseType 'blob' comes
    // back as a Blob, not parsed JSON, so it can't be read the same way as other requests.
    const handleDownloadPdf = async () => {
        setDownloadingPdf(true);
        try {
            const blob = await IncidentReportService.getPdf(orgId, report.id);
            downloadBlob(blob, `${report.report_no}.pdf`);
        } catch (e) {
            SnackbarNotify.error('Failed to download the PDF.');
        } finally {
            setDownloadingPdf(false);
        }
    };

    // closeOnConfirm=false — ConfirmModal only closes when we explicitly clear
    // confirmAction, so a failed attempt leaves the dialog open for a retry.
    const handleConfirmedAction = async () => {
        const isClose = confirmAction === 'close';
        try {
            const updated = isClose
                ? await IncidentReportService.close(orgId, report.id)
                : await IncidentReportService.approve(orgId, report.id);
            applyUpdated(updated);
            SnackbarNotify.success(isClose ? 'Report closed.' : 'Report sent to the manager.');
            setConfirmAction(null);
            onClose();
        } catch (e) {
            SnackbarNotify.error(errorMessageFrom(e));
            if (e?.response?.status === 409) refreshReport();
        }
    };

    const parties = report?.parties || [];
    const vehicles = report?.vehicles || [];
    const witnesses = report?.witnesses || [];
    const gateEvents = report?.linked_access_logs || [];
    const photos = report?.photos || [];
    const narrative = report?.narrative || {};
    const reviewHistory = report?.review_history || [];

    const violationParty = report?.violation_user_id
        ? parties.find(p => p.user_id === report.violation_user_id)
        : null;

    const isSupervisorStage = report?.review_stage === 'supervisor';
    const showApprove = isSupervisorStage && canManage;
    const showEdit = isSupervisorStage && canManage;
    const showMarkViolation = isSupervisorStage && canManage && !report?.is_violation;
    const showRemoveViolation = isSupervisorStage && canManage && report?.is_violation;
    const showAcknowledge =
        report?.status === 'under_review' && report?.review_stage === 'manager' && canView;
    const showClose =
        ((report?.status === 'under_review' && report?.review_stage === 'manager') ||
            report?.status === 'acknowledged') &&
        canView;
    const hasActions =
        canView ||
        showApprove ||
        showEdit ||
        showMarkViolation ||
        showRemoveViolation ||
        showAcknowledge ||
        showClose;

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            keepMounted
            TransitionComponent={PopupTransition}
            open={open}
            onClose={onClose}
        >
            {loading || !report ? (
                <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <DialogTitle sx={{ position: 'relative', pr: 6 }}>
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            flexWrap="wrap"
                            rowGap={1}
                        >
                            <Typography variant="h5">{report.report_no}</Typography>
                            <Chip
                                size="small"
                                color={severityColor(report.severity)}
                                label={incidentSeverityLabel(report.severity)}
                            />
                            {report.status && (
                                <Chip
                                    size="small"
                                    color={statusColor(report.status)}
                                    label={humanize(report.status)}
                                />
                            )}
                            {report.is_violation && (
                                <Chip size="small" color="error" label="Violation" />
                            )}
                        </Stack>

                        {report.status === 'under_review' && report.review_stage && (
                            <Typography variant="caption" color="secondary" display="block" sx={{ mt: 0.5 }}>
                                On the {humanize(report.review_stage)}&apos;s desk
                            </Typography>
                        )}

                        <IconButton
                            onClick={onClose}
                            sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
                        >
                            <CloseOutlined fontSize="small" />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent dividers>
                        <Stack spacing={2.5}>
                            {report.is_violation && (
                                <Alert severity="warning">
                                    {report.violation_user_id
                                        ? `Flagged as a violation — linked to ${
                                              capitalizeFirstLetterMulti(violationParty?.name) ||
                                              report.violation_user_id
                                          }`
                                        : 'Flagged as a violation — no linked account'}
                                </Alert>
                            )}

                            <SectionCard icon={InfoOutlined} title="Summary">
                                <Grid container spacing={2}>
                                    <DetailField label="Category">
                                        {`${incidentCategoryLabel(
                                            report.category
                                        )} · ${incidentSubtypeDetail(
                                            report.sub_type,
                                            report.sub_type_other
                                        )}`}
                                    </DetailField>
                                    <DetailField label="Time observed">
                                        {humanize(report.time_observed)}
                                    </DetailField>
                                    <DetailField label="Occurred">
                                        {formatDateTime(report.occurred_at)}
                                    </DetailField>
                                    <DetailField label="Reported">
                                        {formatDateTime(report.reported_at)}
                                    </DetailField>
                                    <DetailField label="Post / Location">
                                        {[
                                            report.post_label,
                                            report.location_label,
                                            report.location_detail
                                        ]
                                            .filter(Boolean)
                                            .join(' — ')}
                                    </DetailField>
                                    <DetailField label="Guard">
                                        {capitalizeFirstLetterMulti(report.guard_name)}
                                    </DetailField>
                                </Grid>
                            </SectionCard>

                            <SectionCard icon={DescriptionOutlined} title="Narrative">
                                <Grid container spacing={2}>
                                    <DetailField label="Observed" full>
                                        {capitalizeFirstLetter(narrative.observed)}
                                    </DetailField>
                                    <DetailField label="Actions taken" full>
                                        {capitalizeFirstLetter(narrative.actions)}
                                    </DetailField>
                                    <DetailField label="Outcome" full>
                                        {capitalizeFirstLetter(narrative.outcome)}
                                    </DetailField>
                                </Grid>
                            </SectionCard>

                            {parties.length > 0 && (
                                <SectionCard icon={GroupsOutlined} title="Parties">
                                    <Stack spacing={1.25}>
                                        {parties.map((p, i) => (
                                            <ListEntry key={i}>
                                                <Typography variant="body1">
                                                    {capitalizeFirstLetterMulti(p.name) ||
                                                        'Unknown person'}
                                                    {' — '}
                                                    {humanize(p.role)}
                                                    {p.source
                                                        ? ` (${humanize(p.source)})`
                                                        : ''}
                                                </Typography>
                                                {p.description && (
                                                    <Typography variant="body2" color="secondary">
                                                        {`Description: ${capitalizeFirstLetter(
                                                            p.description
                                                        )}`}
                                                    </Typography>
                                                )}
                                                {p.last_seen && (
                                                    <Typography variant="body2" color="secondary">
                                                        {`Last seen: ${capitalizeFirstLetter(
                                                            p.last_seen
                                                        )}`}
                                                    </Typography>
                                                )}
                                            </ListEntry>
                                        ))}
                                    </Stack>
                                </SectionCard>
                            )}

                            {vehicles.length > 0 && (
                                <SectionCard icon={DirectionsCarOutlined} title="Vehicles">
                                    <Stack spacing={1.25}>
                                        {vehicles.map((v, i) => (
                                            <ListEntry key={i}>
                                                <Typography variant="body1">
                                                    {v.plate || '—'}
                                                    {v.description
                                                        ? ` — ${capitalizeFirstLetter(
                                                              v.description
                                                          )}`
                                                        : ''}
                                                    {v.source
                                                        ? ` (${humanize(v.source)})`
                                                        : ''}
                                                </Typography>
                                                {(v.owner_name || v.owner_type) && (
                                                    <Typography variant="body2" color="secondary">
                                                        {`Owner: ${[
                                                            capitalizeFirstLetterMulti(
                                                                v.owner_name
                                                            ),
                                                            v.owner_type
                                                        ]
                                                            .filter(Boolean)
                                                            .join(' · ')}`}
                                                    </Typography>
                                                )}
                                            </ListEntry>
                                        ))}
                                    </Stack>
                                </SectionCard>
                            )}

                            {witnesses.length > 0 && (
                                <SectionCard icon={RecordVoiceOverOutlined} title="Witnesses">
                                    <Stack spacing={1.25}>
                                        {witnesses.map((w, i) => (
                                            <ListEntry key={i}>
                                                <Typography variant="body1">
                                                    {capitalizeFirstLetterMulti(w.name) || '—'}
                                                    {w.phone ? ` — ${w.phone}` : ''}
                                                </Typography>
                                                {w.statement && (
                                                    <Typography variant="body2" color="secondary">
                                                        {`Statement: ${capitalizeFirstLetter(
                                                            w.statement
                                                        )}`}
                                                    </Typography>
                                                )}
                                            </ListEntry>
                                        ))}
                                    </Stack>
                                </SectionCard>
                            )}

                            {gateEvents.length > 0 && (
                                <SectionCard icon={MeetingRoomOutlined} title="Linked gate events">
                                    <Stack spacing={1.25}>
                                        {gateEvents.map((g, i) => {
                                            const s = g.snapshot || {};
                                            const who = capitalizeFirstLetterMulti(
                                                [s.first_name, s.last_name]
                                                    .filter(Boolean)
                                                    .join(' ')
                                            );
                                            return (
                                                <ListEntry key={g.access_log_id || i}>
                                                    <Typography variant="body1">
                                                        {s.str || who || '—'}
                                                    </Typography>
                                                    <Typography variant="body2" color="secondary">
                                                        {[
                                                            humanize(s.access_type),
                                                            s.success
                                                                ? 'Granted'
                                                                : 'Denied',
                                                            formatDateTime(s.attempted_at)
                                                        ]
                                                            .filter(v => v && v !== '—')
                                                            .join(' · ')}
                                                    </Typography>
                                                </ListEntry>
                                            );
                                        })}
                                    </Stack>
                                </SectionCard>
                            )}

                            {photos.length > 0 && (
                                <SectionCard icon={PhotoLibraryOutlined} title="Photos">
                                    <Grid container spacing={2}>
                                        {photos.map((photo, i) => (
                                            <Grid item xs={6} sm={4} key={photo.storage_path || i}>
                                                <Box
                                                    component="img"
                                                    src={photo.url}
                                                    alt={`Incident photo ${i + 1}`}
                                                    onClick={() =>
                                                        setLightbox({
                                                            open: true,
                                                            imageSrc: photo.url,
                                                            title: `Incident photo ${i + 1}`,
                                                            meta: [
                                                                formatDateTime(photo.taken_at),
                                                                photo.is_geotagged
                                                                    ? 'geotagged'
                                                                    : null
                                                            ]
                                                                .filter(Boolean)
                                                                .join(' · ')
                                                        })
                                                    }
                                                    sx={{
                                                        width: '100%',
                                                        height: 140,
                                                        objectFit: 'cover',
                                                        borderRadius: 1,
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    color="secondary"
                                                    display="block"
                                                >
                                                    {formatDateTime(photo.taken_at)}
                                                    {photo.is_geotagged ? ' · geotagged' : ''}
                                                </Typography>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </SectionCard>
                            )}

                            <SectionCard icon={VerifiedUserOutlined} title="Attestation">
                                <Grid container spacing={2}>
                                    <DetailField label="Attested">
                                        {report.attested ? 'Yes' : 'No'}
                                    </DetailField>
                                    <DetailField label="Signature">
                                        {capitalizeFirstLetterMulti(report.signature)}
                                    </DetailField>
                                    <DetailField label="Signed by">
                                        {capitalizeFirstLetterMulti(report.guard_name)}
                                    </DetailField>
                                    <DetailField label="Signed at">
                                        {formatDate(report.reported_at)}
                                    </DetailField>
                                </Grid>
                            </SectionCard>

                            {reviewHistory.length > 0 && (
                                <SectionCard icon={HistoryOutlined} title="Review history">
                                    <Stack spacing={1.25}>
                                        {reviewHistory.map((event, i) => (
                                            <ListEntry key={i}>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    flexWrap="wrap"
                                                >
                                                    <Typography variant="body1">
                                                        {humanize(event.action)}
                                                    </Typography>
                                                    <Typography variant="body2" color="secondary">
                                                        {[
                                                            capitalizeFirstLetterMulti(event.by_name),
                                                            formatDateTime(event.at)
                                                        ]
                                                            .filter(Boolean)
                                                            .join(' · ')}
                                                    </Typography>
                                                </Stack>
                                                {event.changed_fields?.length > 0 && (
                                                    <Stack
                                                        direction="row"
                                                        spacing={0.5}
                                                        sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}
                                                    >
                                                        {event.changed_fields.map(field => (
                                                            <Chip
                                                                key={field}
                                                                size="small"
                                                                variant="outlined"
                                                                label={humanize(field)}
                                                            />
                                                        ))}
                                                    </Stack>
                                                )}
                                            </ListEntry>
                                        ))}
                                    </Stack>
                                </SectionCard>
                            )}
                        </Stack>
                    </DialogContent>

                    {hasActions && (
                        <DialogActions sx={{ px: 3, py: 1.5 }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{ flexWrap: 'wrap', rowGap: 1, justifyContent: 'flex-end' }}
                            >
                                {canView && (
                                    <Tooltip title="Download PDF">
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={handleDownloadPdf}
                                                disabled={downloadingPdf}
                                            >
                                                {downloadingPdf ? (
                                                    <CircularProgress size={18} />
                                                ) : (
                                                    <DownloadOutlined fontSize="small" />
                                                )}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                                {showEdit && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={acting}
                                        onClick={() => setEditOpen(true)}
                                    >
                                        Edit
                                    </Button>
                                )}
                                {showMarkViolation && (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="warning"
                                        disabled={acting}
                                        onClick={() => setViolationOpen(true)}
                                    >
                                        Mark as violation
                                    </Button>
                                )}
                                {showRemoveViolation && (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="warning"
                                        disabled={acting}
                                        onClick={handleRemoveViolation}
                                    >
                                        Remove violation
                                    </Button>
                                )}
                                {showAcknowledge && (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={acting}
                                        onClick={handleAcknowledge}
                                    >
                                        Acknowledge
                                    </Button>
                                )}
                                {showClose && (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="error"
                                        disabled={acting}
                                        onClick={() => setConfirmAction('close')}
                                    >
                                        Close report
                                    </Button>
                                )}
                                {showApprove && (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={acting}
                                        onClick={() => setConfirmAction('approve')}
                                    >
                                        Approve
                                    </Button>
                                )}
                            </Stack>
                        </DialogActions>
                    )}
                </>
            )}

            <Lightbox
                open={lightbox.open}
                onClose={closeLightbox}
                title={lightbox.title}
                meta={lightbox.meta}
                imageSrc={lightbox.imageSrc}
            />

            <IncidentReportEditDialog
                open={editOpen}
                report={report}
                orgId={orgId}
                onClose={() => setEditOpen(false)}
                onSaved={applyUpdated}
            />
            <IncidentReportViolationDialog
                open={violationOpen}
                report={report}
                orgId={orgId}
                onClose={() => setViolationOpen(false)}
                onSaved={handleViolationSaved}
            />
            <ConfirmModal
                open={confirmAction !== null}
                onClose={() => setConfirmAction(null)}
                onConfirm={handleConfirmedAction}
                closeOnConfirm={false}
                type={ConfirmModalType.WARNING}
                title={
                    confirmAction === 'close'
                        ? 'Close this report?'
                        : 'Approve and send to the manager?'
                }
                text={
                    confirmAction === 'close'
                        ? 'Closing locks the report permanently — there is no way to reopen it.'
                        : "The report moves to the manager's desk. Editing and violation flags are no longer available after this."
                }
                confirmText={confirmAction === 'close' ? 'Close report' : 'Approve'}
            />
        </Dialog>
    );
};

export default IncidentReportDetailDialog;
